import { apiGet, apiPost, apiPut, apiDelete } from "../_apiShim";
import { withAiContentTool, withSourceEventId } from "@/lib/ai/aiContentQuery";
import type { Filter } from "@/types/Filter";
import type {
  SeatingInitDTO,
  SeatingRoomDTO,
  SeatingFurnitureDTO,
  SeatingStatsDTO,
  SeatingGuestDTO,
  SeatingGroupCount,
  SeatEntry,
  FurnitureType,
  SeatStatus,
  AutoSeatResult,
  SeatingFurniturePresetDTO,
  ApplyPresetResult,
  FurnitureShape,
  SeatHistoryPage,
  SeatHistoryRow,
  CompanionSide,
  SeatCategoriesDTO,
  SeatingBlockDTO,
} from "@/types/Seating";

const BASE_PATH = "/api/v1/seating/";
const TEMPLATE_BASE_PATH = "/api/v1/template/seating/";

function authHeader() {
  return { "Cache-Control": "no-store" };
}

// ── Init ──────────────────────────────────────────────────────────────────────

export async function seatingInit(eventId: string): Promise<SeatingInitDTO> {
  return apiGet<SeatingInitDTO>(
    `${BASE_PATH}${eventId}/init`,
    authHeader(),
    true,
  );
}

// ── Furniture presets ─────────────────────────────────────────────────────────
// Company-scoped, NOT event-scoped: the whole point of "my 10-seat oval" is that
// it outlives the event it was first built in. The paths therefore carry no
// eventId — the company comes from the token, as everywhere else.

const PRESET_PATH = "/api/v1/seating/presets";

export async function listFurniturePresets(): Promise<SeatingFurniturePresetDTO[]> {
  return apiGet<SeatingFurniturePresetDTO[]>(PRESET_PATH, authHeader(), true);
}

export async function createFurniturePreset(
  body: Omit<SeatingFurniturePresetDTO, "presetId">,
): Promise<SeatingFurniturePresetDTO> {
  return apiPost<SeatingFurniturePresetDTO>(PRESET_PATH, body, authHeader(), true);
}

export async function updateFurniturePreset(
  presetId: string,
  body: Partial<Omit<SeatingFurniturePresetDTO, "presetId">>,
): Promise<SeatingFurniturePresetDTO> {
  return apiPut<SeatingFurniturePresetDTO>(`${PRESET_PATH}/${presetId}`, body, authHeader(), true);
}

export async function deleteFurniturePreset(presetId: string): Promise<void> {
  await apiDelete<void>(`${PRESET_PATH}/${presetId}`, authHeader());
}

/**
 * Pull every piece built from a saved shape back into line with it.
 *
 * <p>`dryRun` plans and writes nothing, and an ABSENT flag means plan — the
 * write can rewrite tables across a whole event. `values` are the numbers the
 * operator saw in the preview: presets are company-wide and carry no version, so
 * the server writes what was reviewed rather than re-reading a shape a colleague
 * may have edited in between.
 */
export async function applyFurniturePreset(
  eventId: string,
  body: {
    presetId: string;
    roomId?: string | null;
    dryRun: boolean;
    values: {
      widthCm: number;
      heightCm: number;
      capacity: number;
      shape?: FurnitureShape | null;
      seatGapCm?: number | null;
      chairSizeCm?: number | null;
    };
  },
): Promise<ApplyPresetResult> {
  return apiPost<ApplyPresetResult>(
    `${BASE_PATH}${eventId}/furniture/apply-preset`,
    body,
    authHeader(),
    true,
  );
}

// ── Rooms ─────────────────────────────────────────────────────────────────────

/**
 * Mirrors `SeatingRoomService.CreateRoomRequest` minus `widthM`/`heightM`
 * (a metres convenience the cockpit never sends) and `sortOrder` (ignored —
 * the server appends). No `floorplanUrl`: the picture is a second PUT.
 */
export type CreateRoomBody = {
  name: string;
  widthCm?: number;
  heightCm?: number;
  roundTablePrefix?: string;
  longTablePrefix?: string;
  chairPrefix?: string;
  blockerPrefix?: string;
  roundTableDefaultCapacity?: number;
  longTableDefaultCapacity?: number;
  seatGapCm?: number | null;
  chairSizeCm?: number | null;
  seatWithCompanions?: boolean;
  /** auto | left | right (seats.io slice 1); absent = `auto`. Ignored by a backend from before the field. */
  companionSide?: CompanionSide;
  occupancyExcludedCategories?: string[];
  floorplanOpacity?: number;
};

export async function createRoom(
  eventId: string,
  body: CreateRoomBody,
  /** Art. 50 KI-VO (J6): the transfer's `template_import` ⇒ `?aiContentTool=` (room names are guest content). */
  aiContentTool?: string,
): Promise<SeatingRoomDTO> {
  return apiPost<SeatingRoomDTO>(
    withAiContentTool(`${BASE_PATH}${eventId}/rooms`, aiContentTool),
    body,
    authHeader(),
    true,
  );
}

/** Request-only flags: a partial `$set` cannot clear a field, so removing a
 *  floorplan needs an explicit instruction. An emptied name prefix travels as ""
 *  (blank = $unset) rather than being dropped from the body. */
export type SeatingRoomUpdateBody = Partial<SeatingRoomDTO> & {
  clearFloorplanUrl?: boolean;
  clearSeatGapCm?: boolean;
  clearChairSizeCm?: boolean;
  /** Back to "the room never chose" — only undo says this; the dialog's
   *  slider always has a value. */
  clearFloorplanOpacity?: boolean;
  /** Back to "colour chairs by status" (seats.io Teil 2, Zug 2) — `seatColorFieldId: null` would be dropped. */
  clearSeatColorFieldId?: boolean;
};

export async function updateRoom(
  eventId: string,
  roomId: string,
  body: SeatingRoomUpdateBody,
): Promise<SeatingRoomDTO> {
  return apiPut<SeatingRoomDTO>(
    `${BASE_PATH}${eventId}/rooms/${roomId}`,
    body,
    authHeader(),
    true,
  );
}

/**
 * Which option of the room's colour field each seated guest holds, read with
 * the caller's own scopes (seats.io Teil 2, Zug 2). Event lane only — there is
 * no template path, and the template cockpit never asks.
 */
export async function getSeatCategories(eventId: string, roomId: string): Promise<SeatCategoriesDTO> {
  return apiGet<SeatCategoriesDTO>(
    `${BASE_PATH}${eventId}/rooms/${roomId}/seat-categories`,
    authHeader(),
    true,
  );
}

// ── Blocks (seats.io Teil 2, Zug 3) ───────────────────────────────────────────

/**
 * A new block of a room. Event path only — a template room carries no blocks.
 * Name and area are trimmed by the server (1..40 characters each, the name
 * unique in the room, case-insensitive); `color` is `#RRGGBB`.
 */
export interface CreateRoomBlockBody {
  name: string;
  areaName?: string;
  color?: string;
  capacityTarget?: number;
}

/**
 * A block edit. Absent = unchanged; a `clear*` flag unsets the field (the
 * room PUT's `clearFloorplanUrl` idiom — a null cannot clear in a partial
 * write).
 */
export interface UpdateRoomBlockBody {
  name?: string;
  areaName?: string;
  clearAreaName?: boolean;
  color?: string;
  clearColor?: boolean;
  capacityTarget?: number;
  clearCapacityTarget?: boolean;
}

/**
 * What a block write answers: the room as it stands after the write (its
 * `version` is NOT bumped — the block endpoints never touch it) and the pieces
 * the write touched. A rename re-stamps the pieces' `blockName`/`areaName`
 * cache WITHOUT a version bump; a delete unsets their `blockId` WITH one, and
 * the cockpit takes those versions at once (`roomSpacingRestamp.ts` pattern).
 */
export interface RoomBlockWriteResult {
  room: SeatingRoomDTO;
  furniture: SeatingFurnitureDTO[];
}

export async function createRoomBlock(
  eventId: string,
  roomId: string,
  body: CreateRoomBlockBody,
): Promise<{ room: SeatingRoomDTO; block: SeatingBlockDTO }> {
  return apiPost<{ room: SeatingRoomDTO; block: SeatingBlockDTO }>(
    `${BASE_PATH}${eventId}/rooms/${roomId}/blocks`,
    body,
    authHeader(),
    true,
  );
}

export async function updateRoomBlock(
  eventId: string,
  roomId: string,
  blockId: string,
  body: UpdateRoomBlockBody,
): Promise<RoomBlockWriteResult> {
  return apiPut<RoomBlockWriteResult>(
    `${BASE_PATH}${eventId}/rooms/${roomId}/blocks/${blockId}`,
    body,
    authHeader(),
    true,
  );
}

/** Idempotent on the server: an unknown id answers 200 and still clears pieces that name it. */
export async function deleteRoomBlock(
  eventId: string,
  roomId: string,
  blockId: string,
): Promise<RoomBlockWriteResult> {
  return apiDelete<RoomBlockWriteResult>(
    `${BASE_PATH}${eventId}/rooms/${roomId}/blocks/${blockId}`,
    authHeader(),
    undefined,
    true,
  );
}

export async function deleteRoom(
  eventId: string,
  roomId: string,
): Promise<void> {
  await apiDelete<void>(
    `${BASE_PATH}${eventId}/rooms/${roomId}`,
    authHeader(),
  );
}

export async function bulkDeleteRooms(
  eventId: string,
  roomIds: string[],
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${eventId}/rooms/bulk-delete`,
    { roomIds },
    authHeader(),
    true,
  );
}

export async function duplicateRoom(
  eventId: string,
  roomId: string,
): Promise<{ room: SeatingRoomDTO; furniture: SeatingFurnitureDTO[] }> {
  return apiPost<{ room: SeatingRoomDTO; furniture: SeatingFurnitureDTO[] }>(
    `${BASE_PATH}${eventId}/rooms/${roomId}/duplicate`,
    {},
    authHeader(),
    true,
  );
}

export async function bulkDuplicateRooms(
  eventId: string,
  roomIds: string[],
): Promise<{ room: SeatingRoomDTO; furniture: SeatingFurnitureDTO[] }[]> {
  return apiPost<{ room: SeatingRoomDTO; furniture: SeatingFurnitureDTO[] }[]>(
    `${BASE_PATH}${eventId}/rooms/bulk-duplicate`,
    { roomIds },
    authHeader(),
    true,
  );
}

// ── Furniture ─────────────────────────────────────────────────────────────────

/**
 * The four numbering fields every furniture write body may carry — the same
 * names and values as on `SeatingFurnitureDTO`. Absent = leave alone (update)
 * or "never chose" (create). The server validates them against the piece's
 * type and answers 400 `numberingNotForType` / `invalidNumbering` /
 * `invalidNumberingStart`; it then stamps `seats[].number`/`row` itself.
 */
export type FurnitureNumberingFields = Pick<
  SeatingFurnitureDTO,
  "numbering" | "numberingStart" | "numberingScope" | "rowLabelStyle"
>;

/**
 * Curved chair rows (seats.io Teil 2, Zug 4) — the three fields a create, a
 * bulk item and an update may carry, the same names and values as on
 * `SeatingFurnitureDTO`: seats per row (front row first, sum = capacity), the
 * bow in whole degrees 0..180, the half-seat stagger. Chair pieces only; the
 * server answers 400 `curvedRowsDisabled` while its switch is off and the body
 * SETS one (0 / false / an unchanged value are allowed), `curveNotForType`,
 * `rowSeatsInvalid`, or `validationError` for a bow outside 0..180. Never on
 * the furniture batch (`FurnitureBatchItem`): the batch carries no geometry.
 */
export type FurnitureCurveFields = Pick<SeatingFurnitureDTO, "rowSeats" | "curveDeg" | "rowStagger">;

/**
 * `$unset` for each numbering field — a partial update cannot clear a field by
 * omitting it. A clear flag wins over a value sent beside it. Clearing all four
 * puts the piece back on legacy numbering (`seatIndex plus one`).
 */
export type FurnitureNumberingClearFlags = {
  clearNumbering?: boolean;
  clearNumberingStart?: boolean;
  clearNumberingScope?: boolean;
  clearRowLabelStyle?: boolean;
};

export async function createFurniture(
  eventId: string,
  roomId: string,
  body: {
    type: FurnitureType;
    name: string;
    x: number;
    y: number;
    /** Degrees clockwise. Absent = 0. The server accepts it since the floor-plan lane landed. */
    rotation?: number;
    widthCm: number;
    heightCm: number;
    capacity: number;
    seatDistribution?: import("@/types/Seating").SeatDistribution | null;
    shape?: import("@/types/Seating").FurnitureShape | null;
    seatGapCm?: number | null;
    chairSizeCm?: number | null;
    /** The saved shape this piece is being placed from. */
    presetId?: string | null;
  } & FurnitureNumberingFields & FurnitureCurveFields,
): Promise<SeatingFurnitureDTO> {
  return apiPost<SeatingFurnitureDTO>(
    `${BASE_PATH}${eventId}/rooms/${roomId}/furniture`,
    body,
    authHeader(),
    true,
  );
}

/** One bulk-create item. Omitted fields are filled by the server (name → auto-numbered, size → fit to seats). */
export type BulkCreateFurnitureItem = {
  type: FurnitureType;
  name?: string;
  x?: number;
  y?: number;
  rotation?: number;
  widthCm?: number;
  heightCm?: number;
  capacity?: number;
  seatDistribution?: import("@/types/Seating").SeatDistribution | null;
  shape?: import("@/types/Seating").FurnitureShape | null;
  category?: string | null;
  /** The operator's own body colour, `#rrggbb`; the server normalises and
   *  refuses anything else. Absent = the default paint. */
  color?: string | null;
  /** The bulk item IS a `CreateFurnitureRequest`: spacing and the preset link travel like on the single create. */
  seatGapCm?: number | null;
  chairSizeCm?: number | null;
  presetId?: string | null;
} & FurnitureNumberingFields & FurnitureCurveFields;

/** The server refuses more per call (`SeatingFurnitureService.MAX_BULK_ITEMS`). */
export const BULK_CREATE_MAX_ITEMS = 200;

/**
 * `POST …/furniture/bulk` — up to 200 pieces in ONE write and ONE rate-limit
 * token. Existed for the assistant's `create_seating_furniture_bulk` tool long
 * before the cockpit had a caller; the floor-plan take-over is the first.
 * The body is a top-level array. Explicit x/y win; without them the server
 * lays the pieces out in a grid.
 */
export async function createFurnitureBulk(
  eventId: string,
  roomId: string,
  items: BulkCreateFurnitureItem[],
  /**
   * Art. 50 Abs. 2 KI-VO: the AI lane behind these pieces (`floorplan_detect`
   * for the floor-plan take-over, `template_import` for a transfer file from
   * a marked event) — sent as `?aiContentTool=`, the body is a bare array.
   * The server stamps the event's guest-content mark from it.
   */
  aiContentTool?: string,
): Promise<import("@/types/Seating").BulkCreateFurnitureResult> {
  return apiPost<import("@/types/Seating").BulkCreateFurnitureResult>(
    withAiContentTool(`${BASE_PATH}${eventId}/rooms/${roomId}/furniture/bulk`, aiContentTool),
    items,
    authHeader(),
    true,
  );
}

// ── Floor-plan detection ─────────────────────────────────────────────────────

/**
 * Ask the server to read the furniture off the room's OWN floor plan (no
 * picture travels — the room already holds it). 202 + jobId; poll the job,
 * then `getFloorplanDetection`. 409 `floorplanDetectAlreadyRunning` while a
 * run is live for the room.
 */
export async function startFloorplanDetect(eventId: string, roomId: string): Promise<{ jobId: string }> {
  const response = await apiPost<{ jobId: string }>(
    `${BASE_PATH}${eventId}/rooms/${roomId}/floorplan/detect`,
    {},
    { ...authHeader(), "Cache-Control": "no-store" },
    true,
  );
  if (!response?.jobId) throw new Error("unknown");
  return response;
}

/** Forget the room's draft (taken over or thrown away). 204; a RUNNING claim is left alone. */
export async function discardFloorplanDetection(eventId: string, roomId: string): Promise<void> {
  await apiDelete<void>(
    `${BASE_PATH}${eventId}/rooms/${roomId}/floorplan/detection`,
    authHeader(),
  );
}

/** The room's latest detection draft. The handler's 404 sentinel (`floorplanDetectionNotFound`) means "none yet". */
export async function getFloorplanDetection(
  eventId: string,
  roomId: string,
): Promise<import("@/types/Seating").FloorplanDetectionDTO> {
  return apiGet<import("@/types/Seating").FloorplanDetectionDTO>(
    `${BASE_PATH}${eventId}/rooms/${roomId}/floorplan/detection`,
    { ...authHeader(), "Cache-Control": "no-store" },
    true,
  );
}

export async function updateFurniture(
  eventId: string,
  furnitureId: string,
  body: Partial<SeatingFurnitureDTO> & FurnitureNumberingClearFlags & {
    version: number; clearSeatDistribution?: boolean; clearSeatGapCm?: boolean; clearChairSizeCm?: boolean; clearCategory?: boolean;
    clearPresetId?: boolean; clearShape?: boolean; clearColor?: boolean; clearBlockId?: boolean;
    /**
     * `$unset` of `rowSeats`, `curveDeg` and `rowStagger` — back to today's
     * grid, `seatDistribution` stays (seats.io Teil 2, Zug 4). Request-only,
     * wins over the three fields beside it, allowed while the switch is off.
     * The undo replay's word; the dialog's "Remove curve" is NOT this (it
     * sends `curveDeg: 0` / `rowStagger: false` and keeps the rows).
     */
    clearCurve?: boolean;
  },
): Promise<SeatingFurnitureDTO> {
  return apiPut<SeatingFurnitureDTO>(
    `${BASE_PATH}${eventId}/furniture/${furnitureId}`,
    body,
    authHeader(),
    true,
  );
}

/**
 * One item of the furniture batch — the drag lane's `x/y/rotation`, the
 * bulk colour lane's `color`/`clearColor` and the bulk numbering lane's four
 * numbering fields + clear flags, each optional: the server leaves an absent
 * field alone and compares `version` per item (a conflict comes back in
 * `conflicts`, never as a 409 for the whole batch). The server validates every
 * item before it writes any (a bad numbering refuses the whole batch with 400).
 */
export interface FurnitureBatchItem extends FurnitureNumberingFields, FurnitureNumberingClearFlags {
  furnitureId: string;
  version: number;
  x?: number;
  y?: number;
  rotation?: number;
  /** `#rrggbb`; the server stores it lowercase. */
  color?: string;
  /** `$unset color` — back to the default paint. Wins over `color`. */
  clearColor?: boolean;
  /**
   * A block of the piece's room (seats.io Teil 2, Zug 3). An id the room does
   * not hold refuses the whole batch with 400 `unknownBlock`. Event path only.
   */
  blockId?: string;
  /** `$unset blockId` (and the server drops the name/area cache with it). Wins over `blockId`. */
  clearBlockId?: boolean;
}

export async function batchUpdateFurniture(
  eventId: string,
  body: { updates: FurnitureBatchItem[] },
): Promise<{ results: SeatingFurnitureDTO[]; conflicts: string[] }> {
  return apiPut<{ results: SeatingFurnitureDTO[]; conflicts: string[] }>(
    `${BASE_PATH}${eventId}/furniture/batch`,
    body,
    authHeader(),
    true,
  );
}

/** What a restore put back, what was already there, and whom it could not seat. */
export interface RestoreFurnitureResult {
  restored: SeatingFurnitureDTO[];
  alreadyPresent: string[];
  dropped: Array<{ furnitureId: string; seatIndex: number; guestId: string; reason: string; elsewhereFurnitureId: string | null }>;
}

/**
 * Put deleted furniture back under its ORIGINAL ids, seats included — the
 * inverse of a delete, for the undo. No fallback onto N creates: that would
 * mint new ids and burn the write budget, which is exactly what this route
 * exists to avoid.
 */
export async function restoreFurniture(
  eventId: string,
  items: SeatingFurnitureDTO[],
): Promise<RestoreFurnitureResult> {
  return apiPost<RestoreFurnitureResult>(
    `${BASE_PATH}${eventId}/furniture/restore`,
    { items },
    authHeader(),
    true,
  );
}

export async function restoreTemplateFurniture(
  templateId: string,
  items: SeatingFurnitureDTO[],
): Promise<{ restored: SeatingFurnitureDTO[]; alreadyPresent: string[] }> {
  return apiPost<{ restored: SeatingFurnitureDTO[]; alreadyPresent: string[] }>(
    `${TEMPLATE_BASE_PATH}${templateId}/furniture/restore`,
    { items },
    authHeader(),
    true,
  );
}

export interface SeatBatchWrite {
  furnitureId: string;
  seatIndex: number;
  status: SeatStatus;
  guestId?: string;
  label?: string;
  /** "" = must be empty; an id = must hold exactly them; absent = unchecked. */
  expectedOccupantId?: string;
}

/**
 * Write many chairs as ONE unit of work, each with its own expectation. A
 * guest who leaves one chair and takes another in the same batch is
 * documented as a move, never mailed a withdrawal. 409 carries the first
 * chair that was not what the caller believed, in `updateSeat`'s body shape.
 */
export async function batchUpdateSeats(
  eventId: string,
  writes: SeatBatchWrite[],
): Promise<{ furniture: SeatingFurnitureDTO[] }> {
  return apiPut<{ furniture: SeatingFurnitureDTO[] }>(
    `${BASE_PATH}${eventId}/seats/batch`,
    { writes },
    authHeader(),
    true,
  );
}

export async function deleteFurniture(
  eventId: string,
  furnitureId: string,
): Promise<void> {
  await apiDelete<void>(
    `${BASE_PATH}${eventId}/furniture/${furnitureId}`,
    authHeader(),
  );
}

export async function bulkDeleteFurniture(
  eventId: string,
  furnitureIds: string[],
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${eventId}/furniture/bulk-delete`,
    { furnitureIds },
    authHeader(),
    true,
  );
}

export async function duplicateFurniture(
  eventId: string,
  furnitureId: string,
): Promise<SeatingFurnitureDTO> {
  return apiPost<SeatingFurnitureDTO>(
    `${BASE_PATH}${eventId}/furniture/${furnitureId}/duplicate`,
    {},
    authHeader(),
    true,
  );
}

// ── Seats ─────────────────────────────────────────────────────────────────────

/**
 * `expectedOccupantId` is a compare-and-set on WHO is sitting there:
 * `""` = the seat must be empty, a guest id = it must be exactly them, absent =
 * unchecked (the server still refuses an assign onto a stranger). A mismatch
 * answers 409 with `SEAT_OCCUPIED` and the current occupant, which is what the
 * conflict dialog renders — send it on every seat write the UI makes, or the
 * dialog is deciding about a seat the operator's screen no longer describes.
 */
export async function updateSeat(
  eventId: string,
  furnitureId: string,
  seatIndex: number,
  body: {
    status: SeatStatus;
    guestId?: string;
    label?: string;
    expectedOccupantId?: string;
  },
): Promise<SeatingFurnitureDTO> {
  return apiPut<SeatingFurnitureDTO>(
    `${BASE_PATH}${eventId}/furniture/${furnitureId}/seats/${seatIndex}`,
    body,
    authHeader(),
    true,
  );
}

export async function resizeTemplateSeats(
  templateId: string,
  furnitureId: string,
  capacity: number,
): Promise<SeatingFurnitureDTO> {
  return apiPost<SeatingFurnitureDTO>(
    `${TEMPLATE_BASE_PATH}${templateId}/furniture/${furnitureId}/seats`,
    { capacity },
    authHeader(), true,
  );
}

/**
 * Resize a piece's seat list. `rowsAware=true` tells the server this build
 * knows chair row blocks (seats.io Teil 2, Zug 4, audit r1 C-1): a cockpit tab
 * from before them resized a row block to columns × rows, then had its grid
 * refused — and every retry grew the back row. The server refuses a resize of
 * a piece with rows (400 `curvedRowsClientTooOld`) unless the marker is there.
 */
export async function resizeSeats(
  eventId: string,
  furnitureId: string,
  capacity: number,
): Promise<SeatingFurnitureDTO> {
  return apiPost<SeatingFurnitureDTO>(
    `${BASE_PATH}${eventId}/furniture/${furnitureId}/seats?rowsAware=true`,
    { capacity },
    authHeader(),
    true,
  );
}

export async function unassignSeat(
  eventId: string,
  furnitureId: string,
  seatIndex: number,
  expectedOccupantId?: string,
): Promise<SeatingFurnitureDTO> {
  return apiPut<SeatingFurnitureDTO>(
    `${BASE_PATH}${eventId}/furniture/${furnitureId}/seats/${seatIndex}`,
    {
      status: "unassigned",
      // Freeing the seat somebody ELSE took while this screen was idle removes
      // the wrong person. Named, the server refuses instead.
      ...(expectedOccupantId !== undefined ? { expectedOccupantId } : {}),
    },
    authHeader(),
    true,
  );
}

/**
 * Free every occupied chair of one table, in ONE request.
 *
 * <p>Not a loop over `unassignSeat`: seat writes are rate-limited (600 per 15
 * minutes, shared with every other seat write on the page), so a ten-seat table
 * would spend ten of them, land ten documentation rows in an order nobody
 * chose, and leave the table half-empty if the budget ran out halfway. One
 * route, one transaction, one answer.
 *
 * <p>`version` is the optimistic lock the rest of this page writes under: a plan
 * left open while a colleague seated somebody must lose the compare-and-set
 * rather than empty a chair it never showed. A backend that does not know this
 * route answers 404 — the caller says so and stops; there is no loop to fall
 * back to.
 *
 * <p>Chairs holding nobody are untouched, labels included: a `reserved` chair
 * saying "Presse" is the operator's own writing, not a guest's.
 */
export async function releaseFurnitureSeats(
  eventId: string,
  furnitureId: string,
  version: number,
): Promise<SeatingFurnitureDTO> {
  return apiPost<SeatingFurnitureDTO>(
    `${BASE_PATH}${eventId}/furniture/${furnitureId}/clear-seats`,
    { version },
    authHeader(),
    true,
  );
}

export type SeatMoveMode = "move" | "swap" | "replace";

export interface SeatMoveRef {
  furnitureId: string;
  seatIndex: number;
}

/**
 * Move a guest between two seats as ONE write.
 *
 * Not two calls: between them either two guests hold one chair or one holds
 * none, and neither state is visible to the operator. And not
 * unassign-then-assign, which documents a departure that never happened and
 * mails the guest that their seat was withdrawn — for a table change.
 */
export async function moveSeat(
  eventId: string,
  body: {
    guestId: string;
    mode: SeatMoveMode;
    from?: SeatMoveRef | null;
    to: SeatMoveRef;
    expected?: { fromOccupantId?: string; toOccupantId?: string };
  },
): Promise<{ furniture: SeatingFurnitureDTO[] }> {
  return apiPost<{ furniture: SeatingFurnitureDTO[] }>(
    `${BASE_PATH}${eventId}/seat-moves`,
    body,
    authHeader(),
    true,
  );
}

/**
 * Seat a whole party — a guest together with their companions — in one call.
 *
 * <p>Two modes, and both refuse to split a family across tables:
 *  - `party`: the operator dropped ONE guest on ONE chair and wants their
 *    companions seated next to them. Not enough chairs at that table ⇒ nobody is
 *    seated and the answer says so.
 *  - `filtered`: seat everybody the rail's current search + filters describe,
 *    each family whole, at one table.
 *
 * Not a loop over {@link updateSeat}: seat writes are rate-limited (600 per 15
 * minutes), so a room of 200 guests would stop dead halfway through, having
 * seated some families and split none of them — visibly — but leaving the rest
 * unseated with no way to finish for a quarter of an hour.
 *
 * `dryRun` plans and writes nothing. Always ask first: every seated guest fires
 * SEAT_ASSIGNED, so one confirm can send as many mails as it fills chairs.
 */
/**
 * Set once a plan request has come back 404 and the fallback carried it: this
 * backend does not know `/auto-seat/plan`. Module-level and never reset — a
 * route does not appear mid-session, and a page load is the natural expiry.
 */
let planRouteMissing = false;

/** Testing seam: the flag above is process-wide and would leak between cases. */
export function __resetPlanRouteProbe() {
  planRouteMissing = false;
}

export async function autoSeat(
  eventId: string,
  body: {
    roomId: string;
    /**
     * `explicit` carries the chairs the operator arranged in the preview, and
     * the server chooses nothing — so a family they split on purpose stays
     * split. It is the only mode that sends `placements`, and it is never a dry
     * run, so it always takes the write path below.
     */
    mode: "party" | "filtered" | "explicit";
    /** explicit mode: the whole plan, chair by chair. Names are not sent. */
    placements?: Array<{ guestId: string; furnitureId: string; seatIndex: number }>;
    guestId?: string;
    furnitureId?: string;
    /**
     * filtered mode: every seat piece of this block of the room, in the
     * planner's table order (seats.io Teil 2, Zug 3). Never beside
     * `furnitureId`; an id the room does not hold answers 400 `unknownBlock`.
     */
    blockId?: string;
    seatIndex?: number;
    query?: string;
    filters?: string;
    /**
     * filtered mode: narrow to ONE section of the grouped rail — the axis in
     * the rail's wire vocabulary and the raw stamped value (`""` = the "(no
     * value)" section). Sent together or not at all; never beside `guestIds`.
     */
    groupBy?: string;
    groupValue?: string;
    dryRun?: boolean;
    status?: SeatStatus;
    /**
     * Seat as many of a party as fit when it fits nowhere whole. Absent means
     * NO — it breaks the one rule the planner exists to keep, so it may only
     * ever travel as an answer the operator gave in the dialog.
     */
    allowPartialParties?: boolean;
    /**
     * Which side of the main guest the companions go, for THIS request only.
     * Absent = the room's `companionSide` (itself absent = `auto`). A backend
     * from before the field ignores it and seats the planner's way.
     */
    companionSide?: CompanionSide;
  },
  /** `signal`: a dry run the caller may abandon (the guest-list editor's preview when its anchor moves on). */
  opts?: { signal?: AbortSignal },
): Promise<AutoSeatResult> {
  // THREE routes, one operation. The server's rate limiter runs as a request
  // filter and never reads the body, so a budget can only ever be hung on a
  // PATH — which is why each of these gestures needs its own:
  //  - `/auto-seat/party`: one drag, one family. An ordinary seating gesture on
  //    the ordinary seat-write budget. Sharing the bulk bucket meant switching
  //    the companion toggle on cut the operator's budget by an order of
  //    magnitude.
  //  - `/auto-seat/plan`: EVERY dry run — the dialog's, and the guest-list
  //    editor's preview of a family's chairs (`mode: "party"`). It writes
  //    nothing (the server forces `dryRun`, and keeps the body's mode), and
  //    the dialog runs one before every confirm and again on every flip of the
  //    split switch — on the bulk bucket each of those was charged like a real
  //    mass seating, and a floor plan of ~30 tables ran out of budget halfway.
  //    The party previews sat on the seat-WRITE bucket instead: one per chair
  //    picked plus one per write's refetch, 3–4 tokens per seated guest, so
  //    after ~150 guests in a quarter hour the real drags answered 429
  //    (audit r1 R1-M10 / L4-F3 / L8-F9).
  //    The price, accepted (audit r2 L2-F6): the previews now spend the
  //    planning bucket (`seating-plan-user`, 400 per 15 min per user), which
  //    the cockpit's auto-seat dialog plans on too. A guest-list session that
  //    previews ~100–130 left/right families inside a quarter hour can make
  //    the dialog's next plan answer 429 and arm its countdown. Both sides
  //    degrade as designed: the editor says "the server picks them when you
  //    seat" and writes without `placements`; the dialog counts down. No
  //    client debounce: a preview is asked once per chair the operator PICKS
  //    (a discrete click, cached 30 s per anchor), and a preview the operator
  //    moved away from is aborted (`opts.signal`) — a delay would only make
  //    every preview later. A bucket of their own is a BE change, not done.
  //  - `/auto-seat`: the runs that actually seat people.
  const plan = body.dryRun === true && !planRouteMissing;
  const ownRoute = body.mode === "party" ? "/party" : "";
  const suffix = plan ? "/plan" : ownRoute;
  try {
    return await apiPost<AutoSeatResult>(
      `${BASE_PATH}${eventId}/auto-seat${suffix}`,
      body,
      authHeader(),
      true,
      undefined,
      opts,
    );
  } catch (err) {
    // A backend from before the plan/apply split does not know `/auto-seat/plan`
    // and answers 404. Falling back keeps the cockpit working through a rollback
    // (and through the window where the frontend deployed first) — the same
    // reasoning, and the same statuses, as the party-route fallback in
    // SeatingClient.tsx. The old path takes the identical body, it just charges
    // the preview to the write budget.
    const status = (err as { status?: number })?.status
      ?? (err as { response?: { status?: number } })?.response?.status;
    if (!plan || (status !== 404 && status !== 405 && status !== 501)) throw err;
    // Remembered for the rest of this page load: during a rollback EVERY
    // preview would otherwise pay two round trips, the first of them only to
    // learn again what we already know.
    planRouteMissing = true;
    // Back to the lane's own route: a party preview to `/auto-seat/party`
    // (where it always went before), never onto the bulk one.
    return apiPost<AutoSeatResult>(
      `${BASE_PATH}${eventId}/auto-seat${ownRoute}`,
      body,
      authHeader(),
      true,
      undefined,
      opts,
    );
  }
}

/**
 * Seat the GUEST LIST's selection: the rows the operator ticked, or every row
 * their filter matches.
 *
 * <p>Its own route, and deliberately WITHOUT the 404 fallback the plan route
 * above has. That fallback exists because the older path does the same thing
 * for a bigger price; here there is no older path that does the same thing at
 * all. A backend that does not know `/auto-seat/selection` would read the same
 * body on `/auto-seat` as "seat every guest in the event" — no query, no
 * filters — and mail every one of them. So a 404 must reach the caller, which
 * says the backend is too old and seats nobody.
 *
 * <p>`guestIds` and `guestFilter` are exclusive: one is the ticked rows, the
 * other the "all matching" banner, which the server resolves under the caller's
 * row scope. `dryRun` decides the route, for the same reason as above — the
 * limiter never reads the body, so the preview needs its own budget.
 */
export type AutoSeatSelectionBody = {
  roomId: string;
  guestIds?: string[];
  guestFilter?: {
    filters: Filter[];
    excludeIds: string[];
    search: string;
    archived: boolean;
    selectAll?: boolean;
  };
  /** Absent = seat whole families, which is what the planner has always done. */
  expandParties?: boolean;
  dryRun: boolean;
  status?: SeatStatus;
  allowPartialParties?: boolean;
  /**
   * ONE table to seat at, instead of the whole room. Absent = automatic: a
   * family with somebody already seated fills in at that member's table, the
   * rest go wherever the planner finds room.
   */
  furnitureId?: string;
  /**
   * Bring the family members who already sit at ANOTHER table of this room
   * over — one write, their old chairs released in it. With `furnitureId`:
   * to that table. Without one ("gather"): each family is brought together at
   * the table where most of it already sits, or moved whole to a table that
   * holds it; a family already together is left alone. Selection lane only —
   * the resource resolves `guestFilter` to ids first, so both selection shapes
   * are fine; the rail's query lane answers 400. A backend from before
   * "gather" answers 400 to the no-table form (`PSeatGuests` maps it).
   * Absent = false.
   */
  moveSeated?: boolean;
  /** Same override as on `autoSeat` — absent = the room's `companionSide`. */
  companionSide?: CompanionSide;
  /** Seat into this block of the room instead of one table (seats.io Teil 2, Zug 3). Never beside `furnitureId`. */
  blockId?: string;
};

export async function autoSeatSelection(
  eventId: string,
  body: AutoSeatSelectionBody,
): Promise<AutoSeatResult> {
  return apiPost<AutoSeatResult>(
    `${BASE_PATH}${eventId}/auto-seat/selection${body.dryRun ? "/plan" : ""}`,
    body,
    authHeader(),
    true,
  );
}

// ── Guests ────────────────────────────────────────────────────────────────────

/**
 * One rail search. `total` counts the asked-for status; `totalByStatus`
 * carries BOTH rail tabs from the same pass (same search, chips and room, only
 * the status left open), so `totalByStatus[status] === total`. Absent on a
 * backend older than the field.
 */
type SeatingGuestSearchResult = {
  guests: SeatingGuestDTO[];
  total: number;
  groups?: SeatingGroupCount[];
  totalByStatus?: { all?: number; unseated?: number };
};

export async function searchSeatingGuests(
  eventId: string,
  query: string,
  status: string,
  filters: string,
  limit: number,
  offset: number,
  roomId?: string | null,
  // LAST on purpose. The seat popups call this too (`searchSeatCandidates`) and
  // pass `roomId` positionally; a new parameter inserted BEFORE it would slide
  // the room id into the groupBy slot silently, because both are strings.
  groupBy?: string | null,
): Promise<SeatingGuestSearchResult> {
  const params = new URLSearchParams({
    q: query,
    status,
    limit: String(limit),
    offset: String(offset),
  });
  if (filters && filters.trim()) params.set("filters", filters);
  // Multi-room: scope seat status to this room so "unseated" means "no seat in
  // THIS room" — a guest seated in another room stays offerable here.
  if (roomId) params.set("roomId", roomId);
  if (groupBy && groupBy.trim()) params.set("groupBy", groupBy);
  return apiGet<SeatingGuestSearchResult>(
    `${BASE_PATH}${eventId}/guests?${params}`,
    authHeader(),
    true,
  );
}

// ── Single guest (full DTO) ────────────────────────────────────────────────

// Fetch one guest's full DTO (profile + additional fields + seat info) — used by the
// seat-hover preview card. Returns null when the guest is not found / out of scope.
export async function getSeatingGuest(
  eventId: string,
  guestId: string,
  // Pass the room the card is being shown in. Without it the server answers
  // event-wide, so the card's seat-status line can describe a seat in a
  // DIFFERENT room than the seat being hovered, and `otherRoomCount` is
  // structurally always 0.
  roomId?: string | null,
): Promise<SeatingGuestDTO | null> {
  const qs = roomId ? `?roomId=${encodeURIComponent(roomId)}` : "";
  return apiGet<SeatingGuestDTO | null>(
    `${BASE_PATH}${eventId}/guests/${guestId}${qs}`,
    authHeader(),
    true,
  );
}

// ── Guest Seat Info ──────────────────────────────────────────────────────────

/**
 * The guest's seats. The response's scalar fields describe the PRIMARY seat and
 * `seats` lists them all — additive, so an older backend that sends only the
 * scalars still renders. Callers must treat `seats` as optional.
 */
export async function getGuestSeatInfo(
  eventId: string,
  guestId: string,
): Promise<(SeatEntry & { seats?: SeatEntry[] | null }) | null> {
  return apiGet<(SeatEntry & { seats?: SeatEntry[] | null }) | null>(
    `${BASE_PATH}${eventId}/guests/${guestId}/seat-info`,
    authHeader(),
    true,
  );
}

// ── Stats ─────────────────────────────────────────────────────────────────────

export async function getSeatingStats(
  eventId: string,
): Promise<SeatingStatsDTO> {
  return apiGet<SeatingStatsDTO>(
    `${BASE_PATH}${eventId}/stats`,
    authHeader(),
    true,
  );
}

// ── Table history ─────────────────────────────────────────────────────────────
// `seating_seat_logs`, furniture-keyed, newest first, keyset-paged. Read-only
// from the cockpit; the rows are written by main-backend and the door worker.

/**
 * Tolerant shape guard. The tab renders whatever the backend sends, so a
 * response with `logs` missing (older backend, proxy error page parsed as
 * JSON) must become an EMPTY page rather than a `.map` on undefined, and a
 * row without an id cannot be keyed or de-duplicated and is dropped.
 */
export function normaliseSeatHistoryPage(raw: unknown): SeatHistoryPage {
  const obj = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const logs = Array.isArray(obj.logs)
    ? (obj.logs as unknown[]).filter(
        (r): r is SeatHistoryRow =>
          !!r && typeof r === "object" && typeof (r as SeatHistoryRow).seatLogId === "string",
      )
    : [];
  const nextCursor = typeof obj.nextCursor === "string" && obj.nextCursor !== "" ? obj.nextCursor : null;
  const hasMore = typeof obj.hasMore === "boolean" ? obj.hasMore : nextCursor !== null;
  return { logs, nextCursor, hasMore };
}

export async function getFurnitureHistory(
  eventId: string,
  furnitureId: string,
  opts: { limit?: number; afterId?: string } = {},
): Promise<SeatHistoryPage> {
  const params = new URLSearchParams();
  if (typeof opts.limit === "number" && opts.limit > 0) params.set("limit", String(opts.limit));
  if (opts.afterId) params.set("afterId", opts.afterId);
  const qs = params.toString();
  const raw = await apiGet<unknown>(
    `${BASE_PATH}${eventId}/furniture/${furnitureId}/history${qs ? `?${qs}` : ""}`,
    authHeader(),
    true,
  );
  return normaliseSeatHistoryPage(raw);
}

// ── Template seating endpoints (no seat-assignment routes) ────────────────────

export async function templateSeatingInit(templateId: string): Promise<Omit<SeatingInitDTO, "stats">> {
  return apiGet<Omit<SeatingInitDTO, "stats">>(
    `${TEMPLATE_BASE_PATH}${templateId}/init`,
    authHeader(),
    true,
  );
}

export async function createTemplateRoom(
  templateId: string,
  body: {
    name: string;
    widthCm?: number;
    heightCm?: number;
    seatGapCm?: number | null;
    chairSizeCm?: number | null;
    roundTablePrefix?: string;
    longTablePrefix?: string;
    chairPrefix?: string;
    blockerPrefix?: string;
    roundTableDefaultCapacity?: number;
    longTableDefaultCapacity?: number;
    seatWithCompanions?: boolean;
    occupancyExcludedCategories?: string[];
    floorplanOpacity?: number;
  },
  /** Art. 50 KI-VO (J6): the transfer's `template_import` ⇒ `?aiContentTool=` — the server stamps the event template's mark. */
  aiContentTool?: string,
  /**
   * J6: "Aus Event importieren" names the SOURCE event (`?sourceEventId=`) —
   * the server copies that event's guest-content mark onto the template.
   */
  sourceEventId?: string,
): Promise<SeatingRoomDTO> {
  return apiPost<SeatingRoomDTO>(
    withSourceEventId(withAiContentTool(`${TEMPLATE_BASE_PATH}${templateId}/rooms`, aiContentTool), sourceEventId),
    body,
    authHeader(),
    true,
  );
}

export async function updateTemplateRoom(
  templateId: string,
  roomId: string,
  body: SeatingRoomUpdateBody,
): Promise<SeatingRoomDTO> {
  return apiPut<SeatingRoomDTO>(
    `${TEMPLATE_BASE_PATH}${templateId}/rooms/${roomId}`,
    body,
    authHeader(),
    true,
  );
}

export async function deleteTemplateRoom(
  templateId: string,
  roomId: string,
): Promise<void> {
  await apiDelete<void>(
    `${TEMPLATE_BASE_PATH}${templateId}/rooms/${roomId}`,
    authHeader(),
  );
}

export async function duplicateTemplateRoom(
  templateId: string,
  roomId: string,
): Promise<{ room: SeatingRoomDTO; furniture: SeatingFurnitureDTO[] }> {
  return apiPost<{ room: SeatingRoomDTO; furniture: SeatingFurnitureDTO[] }>(
    `${TEMPLATE_BASE_PATH}${templateId}/rooms/${roomId}/duplicate`,
    {},
    authHeader(),
    true,
  );
}

export async function createTemplateFurniture(
  templateId: string,
  roomId: string,
  body: {
    type: FurnitureType;
    name: string;
    x: number;
    y: number;
    widthCm: number;
    heightCm: number;
    capacity: number;
    /** Dropped by the import lane until now, so every imported piece landed
     *  unrotated. The server has always accepted it. */
    rotation?: number;
    seatDistribution?: import("@/types/Seating").SeatDistribution | null;
    shape?: import("@/types/Seating").FurnitureShape | null;
    seatGapCm?: number | null;
    chairSizeCm?: number | null;
    /** The saved shape this piece came from — kept so templating does not strip
     *  the link between a table and the shape it was built from. */
    presetId?: string | null;
    /** The operator's own body colour, `#rrggbb`. Same reason as `shape`: the
     *  import lane is the one copy path with its own DTO. */
    color?: string | null;
    /** Normalised server-side like on the event lane (`SeatingFurnitureService.normalizeCategory`). */
    category?: string | null;
  } & FurnitureNumberingFields,
  /** Art. 50 KI-VO (J6): see `createTemplateRoom`. */
  aiContentTool?: string,
  /** J6: see `createTemplateRoom`. */
  sourceEventId?: string,
): Promise<SeatingFurnitureDTO> {
  return apiPost<SeatingFurnitureDTO>(
    withSourceEventId(
      withAiContentTool(`${TEMPLATE_BASE_PATH}${templateId}/rooms/${roomId}/furniture`, aiContentTool),
      sourceEventId,
    ),
    body,
    authHeader(),
    true,
  );
}

export async function updateTemplateFurniture(
  templateId: string,
  furnitureId: string,
  body: Partial<SeatingFurnitureDTO> & FurnitureNumberingClearFlags & { version: number; clearSeatDistribution?: boolean; clearSeatGapCm?: boolean; clearChairSizeCm?: boolean; clearCategory?: boolean; clearShape?: boolean; clearColor?: boolean },
): Promise<SeatingFurnitureDTO> {
  return apiPut<SeatingFurnitureDTO>(
    `${TEMPLATE_BASE_PATH}${templateId}/furniture/${furnitureId}`,
    body,
    authHeader(),
    true,
  );
}

export async function batchUpdateTemplateFurniture(
  templateId: string,
  body: { updates: FurnitureBatchItem[] },
): Promise<{ results: SeatingFurnitureDTO[]; conflicts: string[] }> {
  return apiPut<{ results: SeatingFurnitureDTO[]; conflicts: string[] }>(
    `${TEMPLATE_BASE_PATH}${templateId}/furniture/batch`,
    body,
    authHeader(),
    true,
  );
}

export async function deleteTemplateFurniture(
  templateId: string,
  furnitureId: string,
): Promise<void> {
  await apiDelete<void>(
    `${TEMPLATE_BASE_PATH}${templateId}/furniture/${furnitureId}`,
    authHeader(),
  );
}

export async function duplicateTemplateFurniture(
  templateId: string,
  furnitureId: string,
): Promise<SeatingFurnitureDTO> {
  return apiPost<SeatingFurnitureDTO>(
    `${TEMPLATE_BASE_PATH}${templateId}/furniture/${furnitureId}/duplicate`,
    {},
    authHeader(),
    true,
  );
}
